import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  NotificationService,
  RedirectionUtilityLib,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserSession,
  UserSessionService,
} from '@thehubz/client/core';
import { AccessDto, AuthApiService, CheckUserResDto, LoginDto } from '@thehubz/client/data-access-api';
import { AnyType, MAX_LOGIN_ATTEMPTS_STATUS } from '@thehubz/shared/model';
import { Subject } from 'rxjs';

@Component({
  selector: 'lh-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent implements OnInit, OnDestroy {
  loading = false;
  message?: string;
  disableResetPwdBtn = false;

  // for first page of signin
  signInGroup!: UntypedFormGroup;
  disableSignInBtn = false;
  signInEmail = true;
  loginWithPwd = false;

  // for signin with password page
  signInPwdGroup!: UntypedFormGroup;
  forgotPasswordGroup!: UntypedFormGroup;
  loginAvailable = true;

  maxFailedLoginAttempts = false;

  private readonly _destroying$ = new Subject<void>();

  private handleLogin = {
    next: (data: AccessDto) => {
      if (data && data.access_token) {
        const userSession: UserSession = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          userData: data.user,
          userAccessData: {
            globalRoles: data.accessConfig.globalRoles,
            accessRoles: data.accessConfig.accessRoles as unknown as [string, string[]][],
          },
        };
        this.storageUtility.clearAllSessionStorage(); // cleanup everything here from anonymous user
        this.storageUtility.setLocalStorage(this.userSessionToken, userSession);
        this.userSessionService.setLoggedInState(true);
        this.redirectionUtilityLib.getRedirectUrl((url) => {
          this.router.navigateByUrl(url ?? '/welcome').then(() => {
            window.location.reload(); // brut force to reload the app
          });
        });
      }
    },
    error: (err: AnyType) => {
      if (err.status === 401) {
        this.message = $localize`login failed`;
        const errorMessage = err.error?.message || err.error || '';
        if (errorMessage.includes('[')) {
          // get the number between the brackets
          const number = errorMessage.match(/\[(\d+)\]/);
          if (number && number[1]) {
            const attemptsLeft = parseInt(number[1], 10);
            if (attemptsLeft < 4) {
              this.message += ' ' + attemptsLeft + ' ' + (attemptsLeft === 1 ? $localize`attempt left` : $localize`attempts left`);
            }
          }
        }
      } else if (err.status === MAX_LOGIN_ATTEMPTS_STATUS) {
        this.signInEmail = false;
        this.loginWithPwd = true;
        this.loginAvailable = false;
        this.maxFailedLoginAttempts = true;
      }
      this.loading = false;
    },
  };

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtility: StorageUtilityLib,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthApiService,
    private redirectionUtilityLib: RedirectionUtilityLib,
    private notification: NotificationService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private userSessionService: UserSessionService,
  ) {}

  ngOnInit(): void {
    this.signInGroup = this.formBuilder.group({
      email: ['', [Validators.required]],
    });
    this.signInPwdGroup = this.formBuilder.group({
      login_email_username: ['', Validators.required],
      login_password: ['', Validators.required],
    });
    this.forgotPasswordGroup = this.formBuilder.group({
      email: ['', [Validators.required]],
    });
    this.checkIfHasQueryParam();
  }

  checkIfHasQueryParam() {
    this.route.queryParams.subscribe({
      next: (params) => {
        if (params['email']) {
          this.signInGroup.controls['email'].patchValue(params['email']);
          this.forgotPasswordGroup.controls['email'].patchValue(params['email']);
          this.checkUser();
        } else if (params['redirectUrl']) {
          this.redirectionUtilityLib.setRedirectUrl(params['redirectUrl']);
        }
      },
    });
  }

  checkUser() {
    this.loading = true;
    this.disableSignInBtn = true;
    this.authService.authControllerCheckUserInactiveAndExpiredAndSSO(this.signInGroup.value).subscribe({
      next: (res: CheckUserResDto) => {
        this.loading = false;
        this.disableSignInBtn = false;
        if (res.login) {
          this.signInEmail = false;
          if (!res.sso) {
            this.loginWithPwd = true;
            const email = this.signInGroup.controls['email'].value;
            this.signInPwdGroup.controls['login_email_username'].setValue(email);
          } else {
            // not supported
            this.clientNotificationHandlerService.errorNotification('Your user can not access this client application');
            console.log('user has SSO configured');
          }
        } else {
          if (res.maxFailedLoginAttempts) {
            this.signInEmail = false;
            this.loginWithPwd = true;
            this.loginAvailable = false;
            this.maxFailedLoginAttempts = true;
          } else {
            this.signInGroup.reset();
            this.router.navigate([], { relativeTo: this.route });
          }
        }
      },
      error: (err) => {
        this.clientNotificationHandlerService.errorNotification(err);
        this.loading = false;
        this.disableSignInBtn = false;
        this.signInEmail = true;
        this.loginWithPwd = false;
      },
    });
  }

  emailSignIn() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        email: this.signInGroup.controls['email'].value,
      },
    });
  }

  // ------------------------------------ Signin with pwd ------------------------------------------------------

  signInPwd() {
    const loginObject: LoginDto = {
      username: this.signInPwdGroup.value['login_email_username'],
      password: this.signInPwdGroup.value['login_password'],
      brandingKey: this.env.brandingKey,
    };

    if (loginObject.username && loginObject.password) {
      this.loading = true;
      this.authService.authControllerLogin(loginObject).subscribe(this.handleLogin);
    }
  }

  forgotPassword() {
    this.loading = true;
    this.disableResetPwdBtn = true;
    this.authService.authControllerForgotPassword(this.forgotPasswordGroup.value).subscribe({
      next: (res: { message?: string; error?: string }) => {
        this.loading = false;
        this.disableResetPwdBtn = false;
        if (res.message) {
          this.notification.addNotification({
            type: 'info',
            message: $localize`Password reset link sent to your email`,
          });
          this.router.navigate(['/login']);
        } else if (res.error) {
          this.notification.addNotification({
            type: 'info',
            message: res.error,
          });
        }
      },
      error: (err) => {
        this.loading = false;
        this.disableResetPwdBtn = false;
        this.clientNotificationHandlerService.errorNotification(err);
      },
    });
  }

  clearInput(formControlName: string, formGroup: string) {
    if (formControlName === 'email' || formControlName === 'login_email_username') {
      this.signInGroup.reset();
      this.signInPwdGroup.reset();
      this.forgotPasswordGroup.reset();
      this.loginWithPwd = false;
      this.signInEmail = true;
      this.loginAvailable = true;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        queryParams: {
          email: null,
        },
      });
    }

    if (formControlName === 'login_password' && formGroup === 'signInPwdGroup') {
      this.signInPwdGroup.controls[formControlName].reset();
    }
  }

  redirectToSignUp() {
    this.router.navigate(['/sign-up']);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
